import React, { useMemo, useState, useRef, useEffect } from 'react';
import { ReportViewModel } from 'types/report';
import './ChartModule.scss';
import Chart from './Chart';
import TransactionsTableContainer from '../TransactionsTable/TransactionsTableContainer';
import useAttachmentsModal, { AttachmentMetadata } from 'hooks/useAttachmentsModal';
import { useLocationsStore } from 'hooks/useLocationsStore';
import AttachmentModal from 'components/Attachments/AttachmentModal';
import { IconForwardBtn, IconUp, IconDown } from 'components/Icons';
import { rawData } from '../TransactionsTable/mock';
import { renderDate } from 'lib';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { EnhancedAskRequest } from 'hooks/useTransactionDetails';

interface ChartModuleProps {
  chartData?: ReportViewModel | null;
  initialFilters?: {
    actuals: boolean;
    budget: boolean;
    variance: boolean;
    money: boolean;
    percentage: boolean;
  };
  selectedRow?: string | null;
  onClose?: () => void;
  onSelectionChange?: (rowId: string) => void;
  selectedColumn?: number | string | null;
  onColumnSelectionChange?: (columnId: number | string | null) => void;
  // For data passed from RightPanelContainer
  data?: {
    chartData: ReportViewModel | null;
    initialFilters?: {
      actuals: boolean;
      budget: boolean;
      variance: boolean;
      money: boolean;
      percentage: boolean;
    };
    selectedRow?: string | null;
    selectedColumn?: number | string | null;
    reversed?: boolean;
  };
}

/**
 * Chart Module for Right Panel
 *
 * Displays chart visualization based on the current data selection
 * Uses the ChartContext to manage chart state internally
 */
const ChartModule: React.FC<ChartModuleProps> = props => {
  const {
    showAttachmentImage,
    attachmentsUrl,
    attachmentMetadata,
    isLoading,
    handleCloseAttachmentImage,
    getAttachmentsUrl,
  } = useAttachmentsModal();

  const { enableRightSideTransactions } = useFlags();

  const chartData = useMemo(() => {
    const innerChartData = props.chartData || (props.data && props.data.chartData);
    return innerChartData;
  }, [props.chartData, props.data]);
  // Calculate chart height based on panel size
  const [chartHeight, setChartHeight] = useState(260);
  const [tableHeight, setTableHeight] = useState(280);
  const [isDragging, setIsDragging] = useState(false);
  const [isChartExpanded, setIsChartExpanded] = useState(true);

  const { currentLocationId, allLocations } = useLocationsStore();

  // rAF schedulers to coalesce many mousemove events
  const chartHeightRafId = useRef<number | null>(null);
  const pendingChartHeightRef = useRef<number | null>(null);
  const tableHeightRafId = useRef<number | null>(null);
  const pendingTableHeightRef = useRef<number | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartHeightRafId.current !== null) cancelAnimationFrame(chartHeightRafId.current);
      if (tableHeightRafId.current !== null) cancelAnimationFrame(tableHeightRafId.current);
    };
  }, []);

  // Extract props from either direct props or nested data prop
  const reversed = useMemo(() => props.data && props.data.reversed, [props.data]);

  const initialFilters = useMemo(
    () => props.initialFilters || (props.data && props.data.initialFilters),
    [props.initialFilters, props.data]
  );
  const selectedRow = useMemo(
    () => props.selectedRow || (props.data && props.data.selectedRow),
    [props.selectedRow, props.data]
  );

  const onClose = props.onClose;
  const onSelectionChange = props.onSelectionChange;
  // Backend handles column reversal - data already in correct order
  const selectedColumn = useMemo(
    () => props.selectedColumn ?? props.data?.selectedColumn ?? null,
    [props.selectedColumn, props.data]
  );

  const onColumnSelectionChange = props.onColumnSelectionChange;

  // Get flagged issues from the data
  const flaggedTransactions = useMemo(() => {
    if (!chartData) return undefined;
    return (chartData as any).queueIssues;
  }, [chartData]);

  const columnData = rawData.columns[(selectedColumn as number) + 1];

  // Resolve location ID from the selected column for multi-location reports
  const resolvedLocationId = useMemo(() => {
    if (!chartData || selectedColumn === null) return currentLocationId;

    // Get the actual column index (accounting for reversed state)
    const actualColumnIndex = (selectedColumn as number) + 1;
    const column = chartData.columns[actualColumnIndex];

    // Phase 3: If this is a multi-location column, extract the location from column metadata
    if (column?.isLocation && column?.reportId) {
      // Phase 3: Use store locations instead of chartData.locations
      // Find location by reportId from the locations store
      const location = allLocations?.find(loc => loc.id === column.reportId);
      return location?.id || currentLocationId;
    }

    // For consolidated or single-location reports, use the main locationId
    return currentLocationId;
  }, [chartData, selectedColumn, currentLocationId, allLocations]);

  const breadcrumb = useMemo(() => {
    if (!columnData) return null;

    if (attachmentMetadata?.date) {
      return (
        <div className="breadcrumb">
          <div>{attachmentMetadata.date}</div>
        </div>
      );
    }

    return (
      <div className="breadcrumb">
        <div>{columnData?.date ? renderDate(columnData.date.start) : null}</div>
        {/* YTD */}
        {columnData?.date && columnData?.date.end === '' ? (
          <>
            <div className="breadcrumb-separator">
              <IconForwardBtn />
            </div>
            <div>{columnData?.date.start}</div>
          </>
        ) : (
          <>
            <div className="breadcrumb-separator">
              <IconForwardBtn />
            </div>
            <div>{columnData?.date ? columnData?.date.end && renderDate(columnData.date.end) : null}</div>
          </>
        )}
      </div>
    );
  }, [rawData, attachmentMetadata]);

  const modalTitle = useMemo(() => {
    return attachmentMetadata ? attachmentMetadata.title : rawData?.name;
  }, [rawData, attachmentMetadata]);

  // Resize handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    const startY = e.clientY;
    const startChartHeight = chartHeight;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startY;
      const newChartHeight = Math.max(200, Math.min(600, startChartHeight + deltaY));
      pendingChartHeightRef.current = newChartHeight;
      if (chartHeightRafId.current === null) {
        chartHeightRafId.current = requestAnimationFrame(() => {
          chartHeightRafId.current = null;
          const next = pendingChartHeightRef.current;
          if (next != null) setChartHeight(next);
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      // Flush any pending update
      if (chartHeightRafId.current !== null) {
        cancelAnimationFrame(chartHeightRafId.current);
        chartHeightRafId.current = null;
      }
      if (pendingChartHeightRef.current != null) {
        setChartHeight(pendingChartHeightRef.current);
        pendingChartHeightRef.current = null;
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Toggle chart visibility
  const toggleChart = () => {
    setIsChartExpanded(!isChartExpanded);
  };

  // Calculate effective chart height (0 when collapsed, normal height when expanded)
  const effectiveChartHeight = isChartExpanded ? chartHeight : 0;

  // Extract chart height based on panel size
  return (
    <div className="right-panel-chart-module">
      <div className="chart-module-placeholder">
        <div className={`chart-section ${!isChartExpanded ? 'hidden' : ''}`} style={{ height: effectiveChartHeight }}>
          <div className="chart-content">
            {chartData && (
              <Chart
                rawData={chartData}
                height={effectiveChartHeight}
                initialFilters={initialFilters}
                selectedRow={selectedRow}
                selectedColumn={selectedColumn}
                reversed={reversed}
                onClose={onClose}
                onSelectionChange={onSelectionChange}
                onColumnSelectionChange={onColumnSelectionChange}
              />
            )}
            {!chartData && (
              <div className="chart-placeholder">
                <p>No data available for chart visualization</p>
              </div>
            )}
          </div>
        </div>

        {/* Combined resize divider with toggle button */}
        <div
          className={`resize-divider ${isDragging ? 'dragging' : ''}`}
          onMouseDown={isChartExpanded ? handleMouseDown : undefined}
        >
          <button
            className={`chart-toggle-button ${isChartExpanded ? 'expanded' : 'collapsed'}`}
            onClick={toggleChart}
            title={isChartExpanded ? 'Hide Chart' : 'Show Chart'}
          >
            {isChartExpanded ? <IconUp /> : <IconDown />}
          </button>
        </div>
      </div>
      {enableRightSideTransactions && (
        <>
          <div className="transactions-table-placeholder">
            <div className="transactions-table-container">
              <TransactionsTableContainer
                rawData={chartData}
                selectedRow={selectedRow}
                selectedColumn={selectedColumn}
                showAttachment={(attachments: string[], attachmentMetadata: AttachmentMetadata) =>
                  getAttachmentsUrl(resolvedLocationId, attachments, attachmentMetadata!)
                }
                flaggedTransactions={flaggedTransactions as EnhancedAskRequest[]}
                resolvedLocationId={resolvedLocationId}
              />
            </div>
            <AttachmentModal
              showAttachmentImage={showAttachmentImage}
              handleClose={handleCloseAttachmentImage}
              attachmentMetadata={attachmentMetadata}
              attachmentsUrl={attachmentsUrl}
              title={modalTitle}
              subtitle={[columnData?.date.start]}
              breadcrumb={breadcrumb}
              isLoading={isLoading}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ChartModule;
